import { JSX, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { NetworkIntegrityAdequacyImpactProviderGap, RouteMatchParams } from "@app/projects/network-assessment/network-integrity/models";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAdequacyImpactType, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { ResourceType } from "@app/resources";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { SearchBox } from "@app/shared/SearchBox";
import { Toggle } from "@app/shared/Toggle";
import { NetworkIntegrityAdequacyImpactGapFlyout } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityAdequacyImpactGapFlyout";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityFilterContainer from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityFilterContainer";
import { StandardColumnWidths } from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityConstants";
import { isProviderHubAvailable, isProviderHubDataReady } from "@app/projects/network-assessment/network-integrity/helpers/providerHubLinkHelper";

interface NetworkIntegrityAdequacyImpactGapTableData {
    [wafflesKeyColumnName]: string;
    state: string;
    county: string;
    countySSA: string;
    zipCode: string;
    zipCodeClassification: string;
    city: string;
    primaryCounty: string;
    specialty: string;
    isNewGap: string;
    details: JSX.Element;
}

const NetworkIntegrityAdequacyImpactGapPage = (): JSX.Element => {
  const aliasMaxLength = 25;
  const ffContext = useContext(FeatureFlagContext);
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const { impactType, projectId, projectRunConfigurationId } = routeMatch.params;
  const isCriticalAlertsEnabled = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_ADEQUACY_IMPACT_CRITICAL_ALERTS);
  const isZipProject = project?.resource?.resourceType === ResourceType.ZipAnalysisProject;
  const currentRunConfiguration = useMemo(
    () => project?.projectRunConfigurations?.find(rc => rc.projectRunConfigurationId === Number(projectRunConfigurationId)),
    [project, projectRunConfigurationId]
  );

  const [providerGaps, setProviderGaps] = useState<NetworkIntegrityAdequacyImpactProviderGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState<string>();
  const [showNewGapsOnly, setShowNewGapsOnly] = useState<boolean>(true);
  const [isDualMembership, setIsDualMembership] = useState<boolean>(false);
  const [primaryProjectRunConfigId, setPrimaryProjectRunConfigId] = useState<number>(Number(projectRunConfigurationId));
  const [secondaryProjectRunConfigId, setSecondaryProjectRunConfigId] = useState<number|null>(null);
  const [primaryMemberAlias, setPrimaryMemberAlias] = useState<string>("");
  const [secondaryMemberAlias, setSecondaryMemberAlias] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string>("");

  const fetchAdequacyImpactGaps = async (runConfigId: number, currentRegionSetIds: number[], currentSpecialtyCodes: string[], signal: AbortSignal): Promise<void> => {
    setLoading(true);
    try {
      const gaps = await networkIntegrityService.getAdequacyImpactProviderGaps(
        Number(projectId),
        runConfigId,
        NetworkIntegrityProviderTypes.Servicing,
        impactType,
        currentRegionSetIds,
        currentSpecialtyCodes,
        signal
      );
      // A newer run configuration or filter set superseded this request, so its response must not win.
      if (signal.aborted) return;
      setProviderGaps(gaps);
      setLoadError("");
    } catch {
      // Aborting rejects the in-flight request; only a real failure is worth surfacing.
      if (!signal.aborted) setLoadError("An error occurred while loading provider gaps. Please try again.");
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!project) return;

    const isDual = currentRunConfiguration?.companionProjectRunConfigurationId != null;

    if (isDual) {
      const companionConfig = project.projectRunConfigurations?.find(
        rc => rc.projectRunConfigurationId === Number(currentRunConfiguration.companionProjectRunConfigurationId)
      );
      const isPrimary = currentRunConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental;
      const primaryConfig = isPrimary ? currentRunConfiguration : companionConfig;
      const secondaryConfig = isPrimary ? companionConfig : currentRunConfiguration;

      const truncateAlias = (alias: string) => alias && alias.length > aliasMaxLength
        ? alias.substring(0, aliasMaxLength - 1) + "…"
        : alias;

      setIsDualMembership(true);
      if (primaryConfig) {
        setPrimaryProjectRunConfigId(primaryConfig.projectRunConfigurationId);
        setPrimaryMemberAlias(truncateAlias(primaryConfig.memberSetAlias));
      }
      if (secondaryConfig) {
        setSecondaryProjectRunConfigId(secondaryConfig.projectRunConfigurationId);
        setSecondaryMemberAlias(truncateAlias(secondaryConfig.memberSetAlias));
      }
    }

    setIsInitialized(true);
  }, [project, projectRunConfigurationId]);

  const effectiveRunConfigId = networkIntegrityContext.projectRunConfigurationDualType === ProjectRunConfigurationType.DualMembershipSupplemental
    ? secondaryProjectRunConfigId ?? primaryProjectRunConfigId
    : primaryProjectRunConfigId;

  useEffect(() => {
    if (!isInitialized || networkIntegrityContext.isLoading) return;

    const abortController = new AbortController();

    void fetchAdequacyImpactGaps(
      effectiveRunConfigId,
      networkIntegrityContext.regionSetIds ?? [],
      networkIntegrityContext.specialtyCodes ?? [],
      abortController.signal
    );

    return () => abortController.abort();
  }, [isInitialized, networkIntegrityContext.isLoading, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.providerType, effectiveRunConfigId])

  // The Provider Hub link targets the run the gaps were fetched under, so it is gated on that run rather than the route's.
  const effectiveRunConfiguration = useMemo(
    () => project?.projectRunConfigurations?.find(rc => rc.projectRunConfigurationId === effectiveRunConfigId),
    [project, effectiveRunConfigId]
  );
  const canLinkToProviderHub = isProviderHubAvailable(ffContext.enabledFeatureFlags.has(Features.PROVIDER_HUB), project, effectiveRunConfiguration);
  const providerHubDataReady = isProviderHubDataReady(project, effectiveRunConfiguration);

  const bulkActionBar = (
    <Toggle
      active={showNewGapsOnly}
      containerClassName="ni-adequacy-gap-listing-page__new-gap-only-toggle"
      label="Display New Gaps Only"
      labelSide="right"
      onChange={() => setShowNewGapsOnly(priorValue => !priorValue) }
      testId="gap-display-new-only-toggle" />
  );

  /* oxlint-disable sort-keys */
  const getTableDataItem = (gap: NetworkIntegrityAdequacyImpactProviderGap): NetworkIntegrityAdequacyImpactGapTableData => {
    return {
      [wafflesKeyColumnName]: isZipProject
        ? `${gap.state}-${gap.zipCode ?? ""}-${gap.specialty}`
        : `${gap.state}-${gap.county}-${gap.specialty}`,
      state: gap.state,
      county: gap.county,
      countySSA: gap.countySSA,
      zipCode: gap.zipCode ?? "",
      zipCodeClassification: gap.zipCodeClassification ?? "",
      city: gap.city ?? "",
      primaryCounty: gap.primaryCounty ?? "",
      specialty: gap.specialty,
      isNewGap: gap.isNewGap ? "Yes" : "No",
      details: <NetworkIntegrityAdequacyImpactGapFlyout
        projectId={projectId}
        projectRunConfigurationId={effectiveRunConfigId.toString()}
        gap={gap}
        isProviderHubAvailable={canLinkToProviderHub}
        isProviderHubDataReady={providerHubDataReady}
                />
    }
  }

  const renderTable = () => {
    let filteredProviderGaps = filterGapsBySearch();
    const totalGaps = filteredProviderGaps.length;

    filteredProviderGaps = filterGapsByNewOnlyToggle(filteredProviderGaps);
    const tableDataGaps = filteredProviderGaps.map((gap) => getTableDataItem(gap));

    // County-only and zip-only columns share slots because they're mutually hidden, so one set of
    // values yields both orders: 
    // County columns/order => State, County, County SSA, Specialty, New Gap
    // Zip columns/order => State, Primary County, County SSA, City, Zip Code Classification, Zip Code, Specialty, New Gap
    const columnConfig: ColumnConfig = {
      state: {
        displayText: "State",
        testId: "gap-state",
        sizeContentToFit: false,
        sortOrder: 10
      },
      county: {
        displayText: "County",
        maxWidth: StandardColumnWidths.countyName,
        minWidth: StandardColumnWidths.countyName,
        testId: "gap-county",
        hidden: isZipProject,
        sortOrder: 20
      },
      primaryCounty: {
        displayText: "Primary County",
        maxWidth: StandardColumnWidths.countyName,
        minWidth: StandardColumnWidths.countyName,
        testId: "gap-primary-county",
        hidden: !isZipProject,
        sortOrder: 20
      },
      countySSA: {
        displayText: "County SSA",
        testId: "gap-county-ssa",
        sizeContentToFit: false,
        sortOrder: 30
      },
      city: {
        displayText: "City",
        maxWidth: StandardColumnWidths.city,
        minWidth: StandardColumnWidths.city,
        testId: "gap-city",
        hidden: !isZipProject,
        sortOrder: 40
      },
      zipCodeClassification: {
        displayText: "Zip Code Classification",
        testId: "gap-zip-code-classification",
        hidden: !isZipProject,
        sortOrder: 50
      },
      zipCode: {
        displayText: "Zip Code",
        testId: "gap-zip-code",
        sizeContentToFit: false,
        hidden: !isZipProject,
        sortOrder: 60
      },
      specialty: {
        displayText: "Specialty",
        testId: "gap-specialty",
        sortOrder: 70
      },
      isNewGap: {
        displayText: "New Gap",
        testId: "gap-is-new-gap",
        sizeContentToFit: false,
        sortOrder: 80
      },
      details: {
        displayText: "",
        testId: "gap-details",
        noSort: true,
        hidden: impactType === NetworkIntegrityAdequacyImpactType.CriticalAlertGaps
          ? !project?.showNetworkIntegrityCriticalAlerts
          : !project?.showNetworkIntegrityAttestationBasedAlerts,
        sortOrder: 90
      }
    };

    const memberAlias = !isDualMembership ? "" : ` (Membership: ${networkIntegrityContext.projectRunConfigurationDualType === ProjectRunConfigurationType.DualMembershipPrimary ? primaryMemberAlias : secondaryMemberAlias})`;

    const downloadReportLink = isCriticalAlertsEnabled && (
      <Link
        className="qes-button qes-button-secondary ms-4"
        type="button"
        id="network-integrity-adequacy-impact-gap__download-report-link"
        to={{
          pathname: `/projects/${projectId}/${effectiveRunConfigId}/network-integrity/adequacy-impact-report/${routeMatch.params.providerType}/${impactType}`,
          state: {
            impactProviderKey: impactType,
            initialProviderType: networkIntegrityContext.providerType,
            regionSetIds: networkIntegrityContext.regionSetIds ?? [],
            reportRowCount: filteredProviderGaps.length,
            runConfiguration: networkIntegrityContext.projectRunConfiguration,
            searchText: search ?? "",
            showNewGapsOnly: showNewGapsOnly,
            specialtyCodes: networkIntegrityContext.specialtyCodes ?? [],
          }
        }}
      >
        Download Report
      </Link>
    );

    return (
      <div className="row">
        <div className="col-12">
          {!loading && (
            <p className="mb5">Displaying {filteredProviderGaps.length} of {totalGaps} total gaps{memberAlias}</p>
          )}
          <Waffles<NetworkIntegrityAdequacyImpactGapTableData>
            bulkActionBar={<>{downloadReportLink}{bulkActionBar}</>}
            columns={columnConfig}
            data={tableDataGaps}
            testId={`${impactType}-gap-table`}
            loading={loading}
            />
        </div>
      </div>
    );
  }
  /* oxlint-enable sort-keys */

  const filterGapsBySearch = (): NetworkIntegrityAdequacyImpactProviderGap[] => {
    if(!search || search.trim() === "") return providerGaps;

    return providerGaps.filter((gap) => {
      const term = search.toLowerCase();
      return gap.state?.toLowerCase().includes(term) ||
        gap.county?.toLowerCase().includes(term) ||
        gap.specialty?.toLowerCase().includes(term) ||
        gap.countySSA?.toLowerCase().includes(term) ||
        gap.zipCode?.toLowerCase().includes(term) ||
        gap.zipCodeClassification?.toLowerCase().includes(term) ||
        gap.city?.toLowerCase().includes(term) ||
        gap.primaryCounty?.toLowerCase().includes(term);
    });
  }

  const filterGapsByNewOnlyToggle = (gaps: NetworkIntegrityAdequacyImpactProviderGap[]): NetworkIntegrityAdequacyImpactProviderGap[] => {
    if (!showNewGapsOnly) return gaps; //show all gaps; not only new gaps

    return gaps.filter((gap) => { return gap.isNewGap });
  }

  const getTitle = () => {
    let title = "Adequacy Impact: "
    switch(impactType){
      case NetworkIntegrityAdequacyImpactType.CriticalAlertGaps:
        title += "Critical Alerts Removed";
        break;
      case NetworkIntegrityAdequacyImpactType.AlertGaps:
        title += isCriticalAlertsEnabled ? "All Alerts Removed" : "Alerts Removed";
        break;
      case NetworkIntegrityAdequacyImpactType.AlertOrWarningGaps:
        title += "Alerts and Warnings Removed";
        break;
    }

    return title;
  }

  const onSearchChange = useCallback((value: string) => {
    if (value.trim().toLowerCase() === search?.trim().toLowerCase()) { return; }
    setSearch(value);
  }, [search]);
  return (
    <Page documentTitle="Adequacy Impact">
      <PageInner>
        <PageHeader title={getTitle()}>
          <PageHeaderRight>
            <Link
              className="qes-button qes-button-secondary"
              type="button"
              id="network-integrity-adequacy-impact-gap__back"
              to={{
                pathname: `/projects/${projectId}/network-integrity`,
                state: {
                  initialProviderType: networkIntegrityContext.providerType,
                  initialRegionSetIds: networkIntegrityContext.regionSetIds,
                  initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                  runConfiguration: networkIntegrityContext.projectRunConfiguration,
                }
              }}
            >
              Return to Project
            </Link>
          </PageHeaderRight>
        </PageHeader>

        <PageSection>
          <div className="col-12 col-lg-8 ps-0">
            <SearchBox
              fieldsetClass="d-flex"
              inputLabel="Search"
              onSubmit={onSearchChange}
              testId="network-integrity-adequacy-impact-gaps__search"
            />
          </div>
        </PageSection>

        <PageSection>
          <NetworkIntegrityFilterContainer isGapDetailPage/>
        </PageSection>

        <PageSection>
          {!!loadError && <QESAlert classNames="col-12" type="danger" persist={true}>{loadError}</QESAlert>}
          {renderTable()}
        </PageSection>
      </PageInner>
    </Page>
  );
}

export { NetworkIntegrityAdequacyImpactGapPage };